// framework/client/components/CheckpointManager.js
import { ComponentBase } from "../core/ComponentBase.js";
import { DataForm } from "./DataForm.js";

export class CheckpointManager extends ComponentBase {
    static styles = `
        /* --- Styles for the trigger button --- */
        .checkpoint-manager-trigger .btn {
            padding: 0.3rem 0.6rem; /* Make it compact */
            line-height: 1;
        }

        /* --- Styles for the main window (unchanged) --- */
        .checkpoint-manager-window {
            position: fixed; z-index: var(--ab-z-overlay); display: none; flex-direction: column;
            border: 1px solid var(--bs-border-color); border-radius: var(--bs-border-radius-lg);
            box-shadow: var(--bs-box-shadow-lg); background-color: var(--bs-body-bg);
            width: 90vw; max-width: 800px; height: 80vh; top: 10%; left: 5%;
        }
        .checkpoint-manager-window.visible { display: flex; }
        .cm-header {
            display: flex; align-items: center; padding: 0.5rem 1rem;
            background-color: var(--bs-tertiary-bg); border-bottom: 1px solid var(--bs-border-color);
            cursor: move; flex-shrink: 0;
        }
        .cm-header-title { font-weight: 500; flex-grow: 1; }
        .cm-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; padding: 1rem; }
        .cm-scope-selector { display: flex; gap: 0.5rem; align-items: center; margin-bottom: 1rem; flex-shrink: 0; flex-wrap: wrap; }
        .cm-status-area {
            padding: 0.75rem; margin-bottom: 1rem; border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius); background-color: var(--bs-tertiary-bg);
            flex-shrink: 0;
        }
        .cm-history-container { flex-grow: 1; overflow-y: auto; }
        .cm-history-list { list-style: none; padding: 0; }
        .cm-history-item {
            padding: 0.75rem; border-bottom: 1px solid var(--bs-border-color-translucent);
            display: flex; flex-direction: column; gap: 0.25rem;
        }
        .cm-history-item:last-child { border-bottom: none; }
        .cm-item-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem; }
        .cm-item-message { font-weight: 500; }
        .cm-item-meta { font-size: var(--ab-text-sm); color: var(--bs-secondary-color); }
        .cm-item-hash { font-family: monospace; }
        .cm-item-actions { margin-top: 0.5rem; }
        .cm-item-tag { font-size: var(--ab-text-base); font-weight: 600; }
        .cm-item-tag .badge { font-size: var(--ab-text-sm); }
        .is-head .cm-item-tag { color: var(--bs-success); }
        .is-head .cm-item-message { color: var(--bs-success); }

        @media (max-width: 992px) {
            .checkpoint-manager-window {
                width: 100vw; height: 100vh; top: 0; left: 0;
                border-radius: 0; max-width: 100vw;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.scopes = [];
        this.selectedScope = null;
        this.checkpoints = [];
        this.status = { isDirty: false, files: [] };
        this.isLoading = false;
        this.isVisible = false;
        this.dragInfo = { active: false, initialX: 0, initialY: 0, initialLeft: 0, initialTop: 0 };

        // --- NEW: DOM element references ---
        this.triggerContainer = null;
        this.windowContainer = null;

        this._boundHandleProjectSelected = this.handleProjectSelected.bind(this);
        this._boundDragMove = this._dragMove.bind(this);
        this._boundDragEnd = this._dragEnd.bind(this);
        this.applyStyles();
    }

    async initialize() {
        this.appClient.events.local.on("projectSelected", this._boundHandleProjectSelected);
        await super.initialize();
    }

    // --- THIS IS THE NEW RENDER METHOD ---
    render() {
        if (!this.triggerContainer) {
            // Create the trigger button container (this is what gets appended to the header)
            this.triggerContainer = document.createElement("div");
            this.triggerContainer.className = "checkpoint-manager-trigger d-inline-block";

            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "btn btn-outline-secondary header-btn position-relative";
            btn.title = "Project Checkpoints";
            btn.innerHTML = '<i class="bi bi-git"></i>';
            btn.addEventListener("click", () => this.show()); // Click opens the window

            this.statusBadge = document.createElement("span");
            this.statusBadge.className = "position-absolute top-0 start-100 translate-middle p-1 bg-warning border border-light rounded-circle";
            this.statusBadge.style.display = "none";
            btn.appendChild(this.statusBadge);

            this.triggerContainer.appendChild(btn);
        }

        // The main window is created but not shown yet
        if (!this.windowContainer) {
            this.renderWindowStructure();
        }

        this.updateStatusBadge();
        return this.triggerContainer;
    }

    updateStatusBadge() {
        if (!this.statusBadge) return;
        // Show badge only if a repo is initialized and has changes
        const hasChanges = this.status && this.status.isDirty;
        this.statusBadge.style.display = hasChanges ? "block" : "none";
    }

    async handleProjectSelected(eventData) {
        const projectId = eventData?.projectData?._id;
        if (this.isVisible && projectId && this.scopes.includes(projectId)) {
            this.selectedScope = projectId;
            const selectEl = this.windowContainer.querySelector('#cm-scope-select');
            if (selectEl) selectEl.value = projectId;
            await this.fetchDataForScope(projectId);
        } else {
            // Even if not visible, fetch the status for the new project to update the badge
            await this.fetchCurrentStatus();
        }
    }

    async fetchCurrentStatus() {
        const projectId = this.appClient.getSharedData('selectedProjectId');
        if (!projectId) {
            this.status = { isDirty: false };
            this.updateStatusBadge();
            return;
        }
        try {
            const status = await this.appClient.rpcClient.call('CheckpointRpc.getStatus', { scope: projectId });
            this.status = status;
        } catch (e) {
            this.status = { isDirty: false };
        }
        this.updateStatusBadge();
    }

    async show() {
        if (!this.windowContainer) this.renderWindowStructure();
        if (!document.body.contains(this.windowContainer)) document.body.appendChild(this.windowContainer);

        this.windowContainer.classList.add('visible');
        this.isVisible = true;
        this.isLoading = true;
        this.renderBody();

        try {
            this.scopes = await this.appClient.rpcClient.call('CheckpointRpc.listScopes');
            const selectedProjectId = this.appClient.getSharedData('selectedProjectId');

            this.selectedScope = (selectedProjectId && this.scopes.includes(selectedProjectId))
                ? selectedProjectId
                : (this.scopes[0] || null);

            await this.fetchDataForScope(this.selectedScope);
        } catch (error) {
            this.log.error("Failed to open Checkpoint Manager:", error);
            this.appClient.notifier.error(error.message);
            this.isLoading = false;
            this.renderBody();
        }
    }

    hide() {
        this.windowContainer.classList.remove('visible');
        this.isVisible = false;
    }

    renderWindowStructure() {
        this.windowContainer = document.createElement('div');
        this.windowContainer.className = 'checkpoint-manager-window';
        this.windowContainer.id = `checkpoint-manager-window-${this.config.instanceId}`;
        this.windowContainer.innerHTML = `
            <div class="cm-header">
                <span class="cm-header-title"><i class="bi bi-git me-2"></i>Checkpoint Manager</span>
                <button id="cm-close-btn" class="btn-close" title="Close"></button>
            </div>
            <div class="cm-body"></div>
        `;
        this.windowContainer.querySelector('.cm-header').addEventListener('mousedown', this._dragStart.bind(this));
        this.windowContainer.querySelector('#cm-close-btn').addEventListener('click', () => this.hide());
    }

    // ... (The rest of the methods: renderBody, fetchDataForScope, showCreateModal, handleCreate, handleRestore, handleTag, handleRollback, drag handlers, destroy)
    // remain exactly the same as the previous implementation. I am including them here for completeness.

    renderBody() {
        const body = this.windowContainer.querySelector('.cm-body');
        body.innerHTML = '';

        const scopeSelector = document.createElement('div');
        scopeSelector.className = 'cm-scope-selector';
        scopeSelector.innerHTML = `
            <label for="cm-scope-select" class="form-label mb-0 fw-bold">Scope:</label>
            <select id="cm-scope-select" class="form-select form-select-sm"></select>
            <button id="cm-refresh-btn" class="btn btn-sm btn-outline-secondary" title="Refresh"><i class="bi bi-arrow-clockwise"></i></button>
        `;
        body.appendChild(scopeSelector);

        const selectEl = scopeSelector.querySelector('#cm-scope-select');
        this.scopes.forEach(scope => {
            const option = document.createElement('option');
            option.value = scope;
            option.textContent = scope;
            if (scope === this.selectedScope) option.selected = true;
            selectEl.appendChild(option);
        });
        selectEl.addEventListener('change', (e) => {
            this.selectedScope = e.target.value;
            this.fetchDataForScope(this.selectedScope);
        });
        scopeSelector.querySelector('#cm-refresh-btn').addEventListener('click', () => this.fetchDataForScope(this.selectedScope));

        if (this.isLoading) {
            body.innerHTML += `<div class="text-center p-5"><div class="spinner-border"></div></div>`;
            return;
        }

        if (!this.selectedScope) {
            body.innerHTML += `<div class="alert alert-info mt-3">No versioned scopes found for this subscription.</div>`;
            return;
        }

        const statusArea = document.createElement('div');
        statusArea.className = 'cm-status-area';
        const statusText = this.status.isDirty
            ? `<span class="text-warning fw-bold">Uncommitted Changes (${this.status.files.length})</span>`
            : `<span class="text-success fw-bold">Clean</span>`;
        statusArea.innerHTML = `
            <div><strong>Status:</strong> ${statusText}</div>
            <button id="cm-create-btn" class="btn btn-success btn-sm mt-2" ${!this.status.isDirty ? 'disabled' : ''}>
                <i class="bi bi-plus-circle-fill"></i> Create New Checkpoint
            </button>
        `;
        statusArea.querySelector('#cm-create-btn').addEventListener('click', () => this.showCreateModal());
        body.appendChild(statusArea);

        const historyContainer = document.createElement('div');
        historyContainer.className = 'cm-history-container';
        const historyList = document.createElement('ul');
        historyList.className = 'cm-history-list';
        historyContainer.appendChild(historyList);
        body.appendChild(historyContainer);

        if (this.checkpoints.length === 0) {
            historyList.innerHTML = `<li class="text-muted text-center p-3">No checkpoints yet.</li>`;
        } else {
            this.checkpoints.forEach(c => {
                const li = document.createElement('li');
                li.className = 'cm-history-item';
                if (c.isHead) li.classList.add('is-head');
                const tagHtml = c.tags.length > 0 ? c.tags.map(t => `<span class="badge bg-info text-dark me-1">${t}</span>`).join('') : '';
                const primaryIdentifier = c.tags.length > 0 ? tagHtml : `<code class="cm-item-hash">${c.hash.substring(0, 7)}</code>`;
                li.innerHTML = `
                    <div class="cm-item-header">
                        <div class="cm-item-tag">${c.isHead ? '<i class="bi bi-check-circle-fill me-1"></i>' : ''}${primaryIdentifier}</div>
                        <div class="cm-item-meta">${new Date(c.date).toLocaleString()}</div>
                    </div>
                    <div class="cm-item-message">${c.message}</div>
                    ${c.tags.length === 0 ? `<div class="cm-item-meta cm-item-hash text-muted">${c.hash}</div>` : ''}
                    <div class="cm-item-actions btn-group btn-group-sm mt-2">
                        <button class="btn btn-outline-primary" data-action="restore" data-hash="${c.hash}">Restore</button>
                        <button class="btn btn-outline-info" data-action="tag" data-hash="${c.hash}">Tag</button>
                        <button class="btn btn-outline-danger" data-action="rollback" data-hash="${c.hash}">Rollback</button>
                    </div>
                `;
                historyList.appendChild(li);
            });
            historyList.addEventListener('click', (e) => {
                const btn = e.target.closest('button[data-action]');
                if (!btn) return;
                const action = btn.dataset.action;
                const hash = btn.dataset.hash;
                if (action === 'restore') this.handleRestore(hash);
                if (action === 'tag') this.handleTag(hash);
                if (action === 'rollback') this.handleRollback(hash);
            });
        }
    }

    async fetchDataForScope(scope) {
        if (!scope) {
            this.status = { isDirty: false, files: [] };
            this.checkpoints = [];
            this.isLoading = false;
            this.renderBody();
            return;
        }
        this.isLoading = true;
        this.renderBody();
        try {
            const [status, checkpoints] = await Promise.all([
                this.appClient.rpcClient.call('CheckpointRpc.getStatus', { scope }),
                this.appClient.rpcClient.call('CheckpointRpc.listCheckpoints', { scope })
            ]);
            this.status = status;
            this.checkpoints = checkpoints;
        } catch (error) {
            this.appClient.notifier.error(`Failed to load data for scope '${scope}': ${error.message}`);
            this.status = { isDirty: false, files: [], error: true };
            this.checkpoints = [];
        } finally {
            this.isLoading = false;
            this.renderBody();
        }
    }

    async showCreateModal() {
        const form = new DataForm({
            appClient: this.appClient, layout: 'dialog', title: 'Create Checkpoint',
            instanceId: `cm-create-form-${this.config.instanceId}`,
            fields: {
                message: { type: 'textarea', label: 'Message', required: true, rows: 3 },
                tagName: { type: 'text', label: 'Tag Name (Optional)', placeholder: 'e.g., v1.0-release' }
            },
            actions: { save: true, cancel: true }
        });
        await form.initialize();
        form.show();
        form.on('dataSaved', async (data) => {
            await this.handleCreate(data.message, data.tagName);
            form.destroy();
        });
        form.on('cancel', () => form.destroy());
    }

    async handleCreate(message, tagName) {
        this.isLoading = true;
        this.renderBody();
        try {
            await this.appClient.rpcClient.call('CheckpointRpc.createCheckpoint', { scope: this.selectedScope, message, tagName });
            this.appClient.notifier.success("Checkpoint created.");
            await this.fetchDataForScope(this.selectedScope);
        } catch (error) {
            this.appClient.notifier.error(`Failed to create checkpoint: ${error.message}`);
        } finally {
            this.isLoading = false;
            this.renderBody();
        }
    }


    async handleTag(hash) {
        const tagName = await this.appClient.notifier.prompt("Enter tag name (e.g., v1.2-final):", { title: "Tag Checkpoint" });
        if (!tagName || !tagName.trim()) return;
        const message = await this.appClient.notifier.prompt("Enter annotation message for the tag:", { title: "Tag Message", inputType: "textarea" });
        if (message === null) return;

        this.isLoading = true;
        this.renderBody();
        try {
            await this.appClient.rpcClient.call('CheckpointRpc.tagRelease', { scope: this.selectedScope, commitHash: hash, tagName, message });
            this.appClient.notifier.success(`Tagged checkpoint ${hash.substring(0, 7)} as '${tagName}'.`);
            await this.fetchDataForScope(this.selectedScope);
        } catch (err) {
            this.appClient.notifier.error(`Tagging failed: ${err.message}`);
        } finally {
            this.isLoading = false;
            this.renderBody();
        }
    }


    async handleRestore(hash) {
        const ok = await this.appClient.notifier.confirm(
            `Restore state from checkpoint ${hash.substring(0, 7)}? This will overwrite uncommitted changes and create a new checkpoint.`,
            { title: "Confirm Restore", okButtonText: "Restore", type: "primary" }
        );
        if (!ok) return;

        this.isLoading = true;
        this.renderBody(); // Show loading spinner

        try {
            const res = await this.appClient.rpcClient.call('CheckpointRpc.restore', { scope: this.selectedScope, hash });

            if (res.success) {
                this.appClient.notifier.success(`Restored to ${hash.substring(0, 7)}. ${res.message || ""}`);
                // Automatically refresh the data and UI
                await this.fetchDataForScope(this.selectedScope);
            } else {
                throw new Error("Restore operation failed on the server.");
            }

        } catch (err) {
            this.appClient.notifier.error(`Restore failed: ${err.message}`);
            // In case of error, we still want to refresh the state to be accurate
            this.isLoading = false;
            await this.fetchDataForScope(this.selectedScope);
        }
    }


    async handleRollback(hash) {
        const ok = await this.appClient.notifier.confirm(
            `<strong>DANGER:</strong> Roll back to checkpoint ${hash.substring(0, 7)}? This will permanently delete all changes made after this point. This action cannot be undone.`,
            { title: "Confirm Destructive Rollback", okButtonText: "Rollback", type: "danger" }
        );
        if (!ok) return;

        this.isLoading = true;
        this.renderBody();

        try {
            const res = await this.appClient.rpcClient.call('CheckpointRpc.rollback', { scope: this.selectedScope, hash });

            // --- START OF FIX ---
            if (res.success) {
                this.appClient.notifier.success(`Rolled back to ${hash.substring(0, 7)}.`);
                // Automatically refresh the data and UI
                await this.fetchDataForScope(this.selectedScope);
            } else {
                throw new Error("Rollback operation failed on the server.");
            }
            // --- END OF FIX ---

        } catch (err) {
            this.appClient.notifier.error(`Rollback failed: ${err.message}`);
            // In case of error, refresh the state to show the user what went wrong
            this.isLoading = false;
            await this.fetchDataForScope(this.selectedScope);
        }
    }



    _dragStart(e) {
        if (e.target.closest('button, select, input')) return;
        this.dragInfo.active = true;
        this.dragInfo.initialX = e.clientX;
        this.dragInfo.initialY = e.clientY;
        this.dragInfo.initialLeft = this.windowContainer.offsetLeft;
        this.dragInfo.initialTop = this.windowContainer.offsetTop;
        document.addEventListener('mousemove', this._boundDragMove);
        document.addEventListener('mouseup', this._boundDragEnd);
    }

    _dragMove(e) {
        if (!this.dragInfo.active) return;
        const dx = e.clientX - this.dragInfo.initialX;
        const dy = e.clientY - this.dragInfo.initialY;
        this.windowContainer.style.left = `${this.dragInfo.initialLeft + dx}px`;
        this.windowContainer.style.top = `${this.dragInfo.initialTop + dy}px`;
    }

    _dragEnd() {
        this.dragInfo.active = false;
        document.removeEventListener('mousemove', this._boundDragMove);
        document.removeEventListener('mouseup', this._boundDragEnd);
    }

    destroy() {
        this.appClient.events.local.off("projectSelected", this._boundHandleProjectSelected);
        document.removeEventListener('mousemove', this._boundDragMove);
        document.removeEventListener('mouseup', this._boundDragEnd);
        this.triggerContainer?.remove();
        this.windowContainer?.remove();
        super.destroy();
    }
}